import React, { useState, useEffect } from 'react';

const EnhancedCompoundQuestion = ({
	question,
	answers,
	onAnswerChange,
	questionIndex
}) => {
	console.log('=== EnhancedCompoundQuestion component rendered ===');
	console.log('Received question prop:', question);
	console.log('Question ID:', question?.id);
	console.log('Question type:', question?.type);
	console.log('Question text length:', question?.text?.length);
	console.log('Question text preview:', question?.text ? question.text.substring(0, 200) + '...' : 'null');
	console.log('Question options count:', question?.options?.length || 0);
	console.log('Question enhancedSections:', question?.enhancedSections);
	console.log('Question answerSections:', question?.answerSections);
	console.log('Full question object keys:', question ? Object.keys(question) : 'null');
	
	// Timer state for countdown
	const [timeRemaining, setTimeRemaining] = useState(question.timeLimit ? question.timeLimit * 60 : null); // Convert to seconds
	const [isTimerRunning, setIsTimerRunning] = useState(true);
	
	// Timer effect for countdown
	useEffect(() => {
		if (!timeRemaining || !isTimerRunning) return;

		const timer = setInterval(() => {
			setTimeRemaining(prev => {
				if (prev <= 1) {
					setIsTimerRunning(false);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);

		return () => clearInterval(timer);
	}, [timeRemaining, isTimerRunning]);

	// Validate required props
	if (!question) {
		return (
			<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
				<div className="text-center text-gray-600">
					<p>Error: No question data provided</p>
				</div>
			</div>
		);
	}

	// Normalize sections: support both enhancedSections and answerSections
	// For compound choice questions, answerSections should be used
	let sections = [];
	console.log('=== Section Detection Logic ===');
	console.log('Question type:', question.type);
	console.log('Is COMPOUND_CHOICE:', question.type === 'COMPOUND_CHOICE');
	console.log('Has answerSections:', !!question.answerSections);
	console.log('answerSections length:', question.answerSections?.length || 0);
	console.log('answerSections is array:', Array.isArray(question.answerSections));
	console.log('Has enhancedSections:', !!question.enhancedSections);
	console.log('enhancedSections length:', question.enhancedSections?.length || 0);
	console.log('enhancedSections is array:', Array.isArray(question.enhancedSections));
	
	// PRIORITY 1: COMPOUND_CHOICE questions should use answerSections
	if (question.type === 'COMPOUND_CHOICE') {
		console.log('Question is COMPOUND_CHOICE, checking answerSections...');
		if (question.answerSections && Array.isArray(question.answerSections) && question.answerSections.length > 0) {
			console.log('✅ Using answerSections for COMPOUND_CHOICE');
			sections = question.answerSections;
		} else {
			console.log('❌ COMPOUND_CHOICE but no valid answerSections found');
			console.log('answerSections:', question.answerSections);
			console.log('answerSections type:', typeof question.answerSections);
			console.log('answerSections is array:', Array.isArray(question.answerSections));
		}
	}
	// PRIORITY 2: Other questions with enhancedSections
	else if (question.enhancedSections && Array.isArray(question.enhancedSections) && question.enhancedSections.length > 0) {
		console.log('✅ Using enhancedSections');
		sections = question.enhancedSections;
	}
	// PRIORITY 3: Fallback to answerSections for other question types
	else if (question.answerSections && Array.isArray(question.answerSections) && question.answerSections.length > 0) {
		console.log('✅ Using answerSections as fallback');
		sections = question.answerSections;
	}
	// PRIORITY 4: No sections found
	else {
		console.log('❌ No sections found in any format');
		console.log('enhancedSections is array:', Array.isArray(question.enhancedSections));
		console.log('enhancedSections length:', question.enhancedSections?.length);
		console.log('answerSections is array:', Array.isArray(question.answerSections));
		console.log('answerSections length:', question.answerSections?.length);
	}
	
	console.log('Final sections array:', sections);
	console.log('Sections length:', sections.length);

	// Debug logging for troubleshooting
	if (sections.length === 0) {
		console.warn('EnhancedCompoundQuestion: No sections found', {
			questionId: question.id,
			questionType: question.type,
			hasEnhancedSections: !!question.enhancedSections,
			enhancedSectionsCount: question.enhancedSections?.length || 0,
			hasAnswerSections: !!question.answerSections,
			answerSectionsCount: question.answerSections?.length || 0,
			questionStructure: Object.keys(question)
		});
		
		// If no sections but we have options, render as a simple multiple choice question
		if (question.options && question.options.length > 0) {
			console.log('Rendering simple multiple choice question (no sections)');
			console.log('Question text:', question.text);
			console.log('Question options:', question.options);
			
			return (
				<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
					{/* Question Text */}
					{question.text && (
						<div className="mb-6">
							<div className="text-gray-900 text-base font-bold leading-relaxed">
								{question.text}
							</div>
						</div>
					)}
					
					{/* Simple Multiple Choice Options */}
					<div className="mb-6">
						<h4 className="text-lg font-semibold text-gray-800 mb-4">
							Select the correct answer(s):
						</h4>
						
						<div className="space-y-3">
							{question.options.map((option, index) => {
								console.log(`Rendering option ${index}:`, option);
								return (
									<label key={option.id || index} className="flex items-start gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
										<input
											type="checkbox"
											className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500 mt-1"
										/>
										<span className="text-gray-800 font-medium">{option.text || option}</span>
									</label>
								);
							})}
						</div>
					</div>
				</div>
			);
		}
		
		// If no sections and no options, render as a simple text question
		if (question.text) {
			console.log('Rendering simple text question (no sections, no options)');
			console.log('Question text:', question.text);
			
			return (
				<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
					{/* Question Text */}
					<div className="mb-6">
						<div className="text-gray-900 text-base font-bold leading-relaxed">
							{question.text}
						</div>
					</div>
					
					{/* Question Info */}
					<div className="bg-gray-50 p-4 rounded-lg">
						<div className="text-sm text-gray-600">
							<p><strong>Question Type:</strong> {question.type}</p>
							<p><strong>Difficulty:</strong> {question.difficulty}</p>
							<p><strong>Marks:</strong> {question.marks}</p>
							{question.remark && (
								<p><strong>Note:</strong> {question.remark}</p>
							)}
						</div>
					</div>
				</div>
			);
		}
		
		// Fallback for questions with no sections, no options, and no text
		return (
			<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
				<div className="text-center text-gray-500">
					<p>No content available for this enhanced compound question.</p>
					<div className="mt-4 text-left bg-gray-50 p-4 rounded-lg">
						<h4 className="font-semibold text-gray-700 mb-2">Question Details:</h4>
						<div className="text-sm text-gray-600">
							<p><strong>ID:</strong> {question.id}</p>
							<p><strong>Type:</strong> {question.type}</p>
							<p><strong>Marks:</strong> {question.marks}</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const handleSectionAnswer = (sectionId, answer) => {
		const currentAnswers = answers[question.id] || {};
		const newAnswers = {
			...currentAnswers,
			[sectionId]: answer
		};
		
		onAnswerChange(question.id, newAnswers);
	};

	const getCurrentAnswers = () => {
		return answers[question.id] || {};
	};

	const getSectionAnswer = (sectionId) => {
		return getCurrentAnswers()[sectionId] || null;
	};

	const isSectionAnswered = (sectionId) => {
		return getSectionAnswer(sectionId) !== null;
	};

	const getAnsweredSectionsCount = () => {
		return Object.keys(getCurrentAnswers()).length;
	};

	const getTotalSections = () => {
		return sections.length;
	};

	const getRequiredSections = () => {
		return sections.filter(section => section.required);
	};

	const getAnsweredRequiredSections = () => {
		const requiredSections = getRequiredSections();
		return requiredSections.filter(section => isSectionAnswered(section.id));
	};

	const isComplete = () => {
		const requiredSections = getRequiredSections();
		return requiredSections.every(section => isSectionAnswered(section.id));
	};

	const getScore = () => {
		if (!isComplete()) return 0;
		
		let totalScore = 0;
		const sectionAnswers = getCurrentAnswers();
		
		sections.forEach(section => {
			const sectionAnswer = sectionAnswers[section.id];
			if (sectionAnswer) {
				const sectionScore = calculateSectionScore(section, sectionAnswer);
				totalScore += sectionScore;
			}
		});
		
		return totalScore;
	};

	const getMaxScore = () => {
		if (question.marks) return question.marks;
		return sections.reduce((sum, section) => sum + (section.marks || 0), 0) || 0;
	};

	const getScorePercentage = () => {
		if (getMaxScore() === 0) return 0;
		return Math.round((getScore() / getMaxScore()) * 100);
	};

	const calculateSectionScore = (section, answer) => {
		if (!answer) return 0;
		switch (section.type) {
			case 'MULTIPLE_CHOICE':
				return calculateMultipleChoiceScore(section, answer);
			case 'SINGLE_CHOICE':
				return calculateSingleChoiceScore(section, answer);
			case 'TRUE_FALSE':
				return calculateTrueFalseScore(section, answer);
			case 'FILL_IN_THE_BLANK':
				return calculateFillInTheBlankScore(section, answer);
			case 'MATCHING':
				return calculateMatchingScore(section, answer);
			case 'ORDERING':
				return calculateOrderingScore(section, answer);
			case 'ACCOUNTING_TABLE':
				return section.marks || 0; // scoring can be custom; default full when provided
			case 'COMPOUND_CHOICE':
				return calculateMultipleChoiceScore(section, answer);
			default:
				return section.marks || 0;
		}
	};

	const calculateMultipleChoiceScore = (section, answer) => {
		const correctOptions = section.options?.filter(opt => opt.isCorrect) || [];
		const userSelected = Array.isArray(answer?.selectedOptions)
			? answer.selectedOptions
			: (Array.isArray(answer) ? answer : [answer]).filter(Boolean);
		if (correctOptions.length === 0) return 0;
		const correctSelected = userSelected.filter(id =>
			correctOptions.some(opt => opt.id === id)
		).length;
		const incorrectSelected = userSelected.filter(id =>
			!correctOptions.some(opt => opt.id === id)
		).length;
		const correctRatio = correctSelected / correctOptions.length;
		const penalty = incorrectSelected * 0.5;
		return Math.max(0, Math.round((correctRatio * (section.marks || 0)) - penalty));
	};

	const calculateSingleChoiceScore = (section, answer) => {
		const correctOption = section.options?.find(opt => opt.isCorrect);
		if (!correctOption) return 0;
		const selected = (typeof answer === 'string' || typeof answer === 'number') ? answer : answer?.selectedOptions?.[0];
		return selected === correctOption.id ? (section.marks || 0) : 0;
	};

	const calculateTrueFalseScore = (section, answer) => {
		const correctAnswer = section.correctAnswer ?? true;
		const picked = (typeof answer === 'boolean') ? answer : (answer === 'true' || answer === true);
		return picked === correctAnswer ? (section.marks || 0) : 0;
	};

	const calculateFillInTheBlankScore = (section, answer) => {
		const correctAnswers = section.correctAnswers || [];
		const userAnswer = Array.isArray(answer) ? answer : [answer];
		if (correctAnswers.length === 0) return 0;
		let correctBlanks = 0;
		correctAnswers.forEach((correct, index) => {
			if (userAnswer[index] && correctAnswers.includes(userAnswer[index])) {
				correctBlanks++;
			}
		});
		const ratio = correctBlanks / correctAnswers.length;
		return Math.round(ratio * (section.marks || 0));
	};

	const calculateMatchingScore = (section, answer) => {
		// answer: { leftId: rightId, ... }
		const pairs = Object.entries(answer || {});
		if (pairs.length === 0) return 0;
		let correct = 0;
		pairs.forEach(([leftId, rightId]) => {
			const leftOpt = section.options?.find(o => o.id === leftId);
			if (!leftOpt) return;
			const targetPairId = (leftOpt.pairId ?? leftOpt.sortOrder) + 1;
			const rightOpt = section.options?.find(o => o.id === rightId);
			if (!rightOpt) return;
			const rightPairId = (rightOpt.pairId ?? rightOpt.sortOrder);
			if (rightPairId === targetPairId) correct++;
		});
		const leftCount = (section.options || []).filter(o => ((o.pairId ?? o.sortOrder) % 2 === 0)).length;
		if (leftCount === 0) return 0;
		return Math.round((correct / leftCount) * (section.marks || 0));
	};

	const calculateOrderingScore = (section, answer) => {
		// answer: [{optionId, order}] or array of ids in order
		const correctOrder = (section.options || [])
			.sort((a, b) => (a.correctOrder ?? a.sortOrder) - (b.correctOrder ?? b.sortOrder))
			.map(o => o.id);
		let userOrder = [];
		if (Array.isArray(answer)) userOrder = answer;
		else if (Array.isArray(answer?.order)) userOrder = answer.order;
		if (userOrder.length !== correctOrder.length) return 0;
		const matches = userOrder.filter((id, idx) => id === correctOrder[idx]).length;
		return Math.round((matches / correctOrder.length) * (section.marks || 0));
	};

	// Format time remaining
	const formatTime = (seconds) => {
		if (!seconds) return '00:00';
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	// Intelligent question type detection
	const detectQuestionType = (section) => {
		// If section has explicit type, use it
		if (section.type) {
			return section.type;
		}

		// Analyze section data to determine type
		if (section.options && Array.isArray(section.options)) {
			// Check if it's multiple choice vs single choice
			const correctOptions = section.options.filter(opt => opt.isCorrect);
			if (correctOptions.length > 1) {
				return 'MULTIPLE_CHOICE';
			} else if (correctOptions.length === 1) {
				return 'SINGLE_CHOICE';
			}
		}

		// Check for specific data patterns
		if (section.scenarios || section.statements) {
			return 'TRUE_FALSE';
		}

		// Enhanced detection for ACCOUNTING_TABLE - check for tableData structure
		if (section.tableData && (section.tableData.headers || section.tableData.rows)) {
			return 'ACCOUNTING_TABLE';
		}

		if (section.items && Array.isArray(section.items)) {
			return 'ORDERING';
		}

		if (section.pairs || (section.leftColumn && section.rightColumn)) {
			return 'MATCHING';
		}

		if (section.correctAnswers || section.blanks) {
			return 'FILL_IN_THE_BLANK';
		}

		// Default fallback
		return 'UNKNOWN';
	};

	// Enhanced section renderer with intelligent type detection
	const renderSection = (section) => {
		try {
			console.log('=== renderSection called ===');
			console.log('Section object:', section);
			console.log('Section title:', section.title);
			console.log('Section type:', section.type);
			console.log('Section options:', section.options);
			console.log('Section options length:', section.options?.length);
			console.log('Section options is array:', Array.isArray(section.options));
			
			const detectedType = detectQuestionType(section);
			const actualType = section.type || detectedType;
			
			console.log(`Section "${section.title}": Detected type: ${detectedType}, Actual type: ${actualType}`);

			const commonProps = {
				section,
				answer: getSectionAnswer(section.id),
				onAnswer: (answer) => handleSectionAnswer(section.id, answer),
				isAnswered: isSectionAnswered(section.id)
			};
			
			console.log('Common props:', commonProps);

			// Enhanced type routing with fallbacks
			switch (actualType.toUpperCase()) {
				case 'MULTIPLE_CHOICE':
				case 'MULTIPLE_CHOICE_QUESTION':
					return <MultipleChoiceSection {...commonProps} />;
					
				case 'SINGLE_CHOICE':
				case 'SINGLE_CHOICE_QUESTION':
				case 'CHOICE':
					return <SingleChoiceSection {...commonProps} />;
					
				case 'TRUE_FALSE':
				case 'TRUE_FALSE_QUESTION':
				case 'BOOLEAN':
					return <TrueFalseSection {...commonProps} />;
					
				case 'FILL_IN_THE_BLANK':
				case 'FILL_IN_THE_BLANK_QUESTION':
				case 'BLANK':
					return <FillInTheBlankSection {...commonProps} />;
					
				case 'MATCHING':
				case 'MATCHING_QUESTION':
				case 'PAIR':
					return <MatchingSection {...commonProps} />;
					
				case 'ORDERING':
				case 'ORDERING_QUESTION':
				case 'SEQUENCE':
				case 'ARRANGE':
					return <OrderingSection {...commonProps} />;
					
				case 'ACCOUNTING_TABLE':
				case 'ACCOUNTING_TABLE_QUESTION':
				case 'TABLE':
				case 'FINANCIAL_TABLE':
					// This will render the table using the tableData structure from enhanced.txt
					return <AccountingTableSection {...commonProps} />;
					
				case 'COMPOUND_CHOICE':
				case 'COMPOUND_CHOICE_QUESTION':
				case 'COMPOUND':
					// For compound choice questions, we need to pass the main question data
					// so the component can access answerSections
					return <CompoundChoiceSection {...commonProps} mainQuestion={question} />;
					
				case 'ESSAY':
				case 'ESSAY_QUESTION':
				case 'LONG_ANSWER':
					return <EssaySection {...commonProps} />;
					
				case 'SHORT_ANSWER':
				case 'SHORT_ANSWER_QUESTION':
				case 'BRIEF_ANSWER':
					return <ShortAnswerSection {...commonProps} />;
					
				case 'NUMERICAL':
				case 'NUMERICAL_QUESTION':
				case 'NUMBER':
					return <NumericalSection {...commonProps} />;
					
				case 'CALCULATION':
				case 'CALCULATION_QUESTION':
				case 'MATH':
					return <CalculationSection {...commonProps} />;
					
				default:
					// Try to render based on detected type as fallback
					console.warn(`Unknown section type: ${actualType}, attempting fallback with detected type: ${detectedType}`);
					
					switch (detectedType.toUpperCase()) {
						case 'MULTIPLE_CHOICE':
							return <MultipleChoiceSection {...commonProps} />;
						case 'SINGLE_CHOICE':
							return <SingleChoiceSection {...commonProps} />;
						case 'TRUE_FALSE':
							return <TrueFalseSection {...commonProps} />;
						case 'FILL_IN_THE_BLANK':
							return <FillInTheBlankSection {...commonProps} />;
						case 'MATCHING':
							return <MatchingSection {...commonProps} />;
						case 'ORDERING':
							return <OrderingSection {...commonProps} />;
						case 'ACCOUNTING_TABLE':
							return <AccountingTableSection {...commonProps} />;
						default:
							return <UnknownSectionType {...commonProps} />;
					}
			}
		} catch (error) {
			console.error('Error rendering section:', error, section);
			return (
				<div className="p-3 text-sm text-red-800">
					Error rendering section: {section.type || 'Unknown'}. {error.message}
				</div>
			);
		}
	};

	return (
		<div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">


			{/* Question Text */}
			{question.text && (
				<div className="mb-6">
					<div className="text-gray-900 text-base font-bold leading-relaxed">
						{question.text}
					</div>
				</div>
			)}

					{/* Sections Container */}
		<div className="mb-6 space-y-6">
			{console.log('Rendering sections container with sections:', sections)}
			{sections.map((section, index) => {
				console.log(`Rendering section ${index}:`, section);
				return (
					<div key={section.id} className="p-6">
						{/* Section Header */}
						<div className="mb-4">
							<div className="flex items-center justify-between mb-2">
								<div className="flex items-center gap-3">
									<h4 className="text-base font-semibold text-gray-800">
										{question.type === 'COMPOUND_CHOICE' ? question.type : `Section ${index + 1}: ${section.title}`}
									</h4>
									{/* Question Type Badge */}
									<span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
										{section.type || detectQuestionType(section)}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<span className="text-sm text-gray-500">
										{section.marks || 0} marks
									</span>
									{section.required && (
										<span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
											Required
										</span>
									)}
									{isSectionAnswered(section.id) && (
										<span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
											✓ Answered
										</span>
									)}
								</div>
							</div>
							{section.question && (
								<p className="text-sm text-gray-600 mb-3">
									{section.question}
								</p>
							)}
						</div>

						{/* Section Content */}
						<div className="mb-4">
							{renderSection(section)}
						</div>
					</div>
				);
			})}
		</div>


			



		</div>
	);
};

// Section Components
const MultipleChoiceSection = ({ section, answer, onAnswer, isAnswered }) => {
	console.log('MultipleChoiceSection rendered with section:', section);
	console.log('Section options:', section.options);
	console.log('Section choices:', section.choices);
	console.log('Section items:', section.items);
	
	const handleOptionChange = (optionId) => {
		const currentAnswer = Array.isArray(answer) ? answer : (answer?.selectedOptions || []);
		let newAnswer;
		if (currentAnswer.includes(optionId)) {
			newAnswer = currentAnswer.filter(id => id !== optionId);
		} else {
			newAnswer = [...currentAnswer, optionId];
		}
		onAnswer({ selectedOptions: newAnswer });
	};

	// Parse options from different data structures
	const getOptions = () => {
		console.log('getOptions called for section:', section.title);
		if (section.options && Array.isArray(section.options)) {
			console.log('Using section.options:', section.options);
			return section.options;
		}
		if (section.choices && Array.isArray(section.choices)) {
			console.log('Using section.choices:', section.choices);
			return section.choices;
		}
		if (section.items && Array.isArray(section.items)) {
			console.log('Using section.items:', section.items);
			return section.items;
		}
		console.log('No options found, returning empty array');
		return [];
	};

	const options = getOptions();
	console.log('Final options array:', options);

	return (
		<div className="space-y-2">
			{options.map((option, index) => (
				<label key={option.id || index} className="flex items-start gap-3 p-2 cursor-pointer hover:bg-gray-50">
					<input 
						type="checkbox" 
						checked={(answer?.selectedOptions || answer || []).includes(option.id || option)} 
						onChange={() => handleOptionChange(option.id || option)} 
						className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" 
					/>
					<span className="text-sm text-gray-700">
						{String.fromCharCode(65 + index)}. {option.text || option}
					</span>
				</label>
			))}
		</div>
	);
};

const SingleChoiceSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Parse options from different data structures
	const getOptions = () => {
		if (section.options && Array.isArray(section.options)) {
			return section.options;
		}
		if (section.choices && Array.isArray(section.choices)) {
			return section.choices;
		}
		if (section.items && Array.isArray(section.items)) {
			return section.items;
		}
		if (section.alternatives && Array.isArray(section.alternatives)) {
			return section.alternatives;
		}
		return [];
	};

	const options = getOptions();

	return (
		<div className="space-y-2">
			{options.map((option, index) => (
				<label key={option.id || index} className="flex items-start gap-3 p-2 cursor-pointer hover:bg-gray-50">
					<input 
						type="radio" 
						name={`section-${section.id}`} 
						value={option.id || option} 
						checked={(answer?.selectedOptions?.[0] || answer) === (option.id || option)} 
						onChange={(e) => onAnswer({ selectedOptions: [e.target.value] })} 
						disabled={isAnswered} 
						className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" 
					/>
					<span className="text-sm text-gray-700">
						{String.fromCharCode(65 + index)}. {option.text || option}
					</span>
				</label>
			))}
		</div>
	);
};

const TrueFalseSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Parse statements from different data structures
	const getStatements = () => {
		if (section.statements && Array.isArray(section.statements)) {
			return section.statements;
		}
		if (section.scenarios && Array.isArray(section.scenarios)) {
			return section.scenarios;
		}
		if (section.questions && Array.isArray(section.questions)) {
			return section.questions;
		}
		if (section.items && Array.isArray(section.items)) {
			return section.items;
		}
		return [];
	};

	// Handle statements array structure
	const statements = getStatements();
	if (statements.length > 0) {
		const handleStatementChange = (statementIndex, value) => {
			// Initialize with empty array if no answer exists
			const currentStatements = answer?.statements || [];
			const newStatements = [...currentStatements];
			newStatements[statementIndex] = value;
			onAnswer({ statements: newStatements });
		};

		return (
			<div className="space-y-4">
				{/* Context and Question */}
				{section.context && (
					<div className="mb-4 p-4">
						<p className="text-sm text-gray-700 leading-relaxed">{section.context}</p>
					</div>
				)}
				
				{section.question && (
					<div className="mb-4">
						<p className="text-base font-bold text-gray-800">{section.question}</p>
					</div>
				)}

				{/* Table-like Structure */}
				<div className="overflow-hidden">
					{/* Header */}
					<div className="bg-gray-100">
						<div className="grid grid-cols-3 gap-4 p-3">
							<div className="col-span-2">
								{/* Left side empty for alignment */}
							</div>
							<div className="flex justify-end gap-8">
								<span className="text-sm font-semibold text-gray-700">Yes</span>
								<span className="text-sm font-semibold text-gray-700">No</span>
							</div>
						</div>
					</div>

					{/* Scenarios */}
					<div className="divide-y divide-gray-200">
						{section.statements.map((statement, index) => (
							<div key={index} className="p-4 hover:bg-gray-50">
								<div className="grid grid-cols-3 gap-4 items-start">
									{/* Scenario Description */}
									<div className="col-span-2">
										<p className="text-sm text-gray-700 leading-relaxed">{statement}</p>
									</div>
									
									{/* Radio Buttons */}
									<div className="flex justify-end gap-8">
										<label className="flex items-center gap-2 cursor-pointer">
											<input 
												type="radio" 
												name={`section-${section.id}-${index}`} 
												value={true} 
												checked={answer?.statements?.[index] === true} 
												onChange={() => handleStatementChange(index, true)} 
												disabled={false} 
												className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500" 
											/>
										</label>
										<label className="flex items-center gap-2 cursor-pointer">
											<input 
												type="radio" 
												name={`section-${section.id}-${index}`} 
												value={false} 
												checked={answer?.statements?.[index] === false} 
												onChange={() => handleStatementChange(index, false)} 
												disabled={false} 
												className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500" 
											/>
										</label>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	// Handle simple true/false
	const handleSimpleChange = (value) => {
		onAnswer(value);
	};

	return (
		<div className="space-y-4">
			{/* Context and Question */}
			{section.context && (
				<div className="mb-4 p-4">
					<p className="text-sm text-gray-700 leading-relaxed">{section.context}</p>
				</div>
			)}
			
			{section.question && (
				<div className="mb-4">
					<p className="text-base font-bold text-gray-800">{section.question}</p>
				</div>
			)}

			{/* Simple True/False Options */}
			<div className="flex gap-4">
				<label className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50">
					<input 
						type="radio" 
						name={`section-${section.id}`} 
						value={true} 
						checked={answer === true || answer === 'true'} 
						onChange={() => handleSimpleChange(true)} 
						disabled={false} 
						className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500" 
					/>
					<span className="text-sm font-medium text-gray-700">True</span>
				</label>
				<label className="flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50">
					<input 
						type="radio" 
						name={`section-${section.id}`} 
						value={false} 
						checked={answer === false || answer === 'false'} 
						onChange={() => handleSimpleChange(false)} 
						disabled={false} 
						className="w-4 h-4 text-gray-600 border-gray-300 focus:ring-gray-500" 
					/>
					<span className="text-sm font-medium text-gray-700">False</span>
				</label>
			</div>
		</div>
	);
};

const FillInTheBlankSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Local state for immediate input responsiveness
	const [localAnswers, setLocalAnswers] = useState({});

	// Initialize local answers from props only when component mounts
	useEffect(() => {
		if (answer) {
			setLocalAnswers(answer);
		} else {
			setLocalAnswers({});
		}
	}, []); // Empty dependency array - only run once on mount

	const handleBlankChange = (blankIndex, value) => {
		// Update local state immediately
		setLocalAnswers(prev => ({
			...prev,
			[blankIndex]: value
		}));
		
		// Save to parent state immediately so answers persist
		const currentAnswer = answer || {};
		const newAnswer = {
			...currentAnswer,
			[blankIndex]: value
		};
		onAnswer(newAnswer);
	};

	const getBlankValue = (blankIndex) => {
		// Use local state first for immediate responsiveness
		if (localAnswers[blankIndex] !== undefined) {
			return localAnswers[blankIndex];
		}
		
		// Fall back to parent state for persistence
		const currentAnswer = answer || {};
		return currentAnswer[blankIndex] || '';
	};

	// Parse the section question text to identify blanks
	const parseQuestionText = (text) => {
		if (!text) return [];
		
		const parts = text.split(/\(\s*\)/g);
		const result = [];
		
		if (parts.length > 1) {
			for (let i = 0; i < parts.length - 1; i++) {
				result.push({
					id: i,
					beforeText: parts[i],
					afterText: parts[i + 1]
				});
			}
		}
		
		return result;
	};

	// Parse [BLANK] placeholders
	const parseBlankPlaceholders = (text) => {
		if (!text) return [];
		
		const parts = text.split(/\[BLANK\]/gi);
		const result = [];
		
		if (parts.length > 1) {
			for (let i = 0; i < parts.length - 1; i++) {
				result.push({
					id: i,
					beforeText: parts[i],
					afterText: parts[i + 1]
				});
			}
		}
		
		return result;
	};

	// Determine number of blanks based on multiple sources
	const getBlanksCount = () => {
		// Check for correctAnswers array
		if (section.correctAnswers && Array.isArray(section.correctAnswers)) {
			return section.correctAnswers.length;
		}
		
		// Check for blanks array
		if (section.blanks && Array.isArray(section.blanks)) {
			return section.blanks.length;
		}
		
		// Check for fields array
		if (section.fields && Array.isArray(section.fields)) {
			return section.fields.length;
		}
		
		// Count () placeholders in question text
		if (section.question) {
			const blankMatches = section.question.match(/\(\s*\)/g);
			if (blankMatches) return blankMatches.length;
		}
		
		// Count [BLANK] placeholders in question text
		if (section.question) {
			const blankMatches = section.question.match(/\[BLANK\]/gi);
			if (blankMatches) return blankMatches.length;
		}
		
		// Count ? placeholders in question text
		if (section.question) {
			const questionMatches = section.question.match(/\?/g);
			if (questionMatches) return questionMatches.length;
		}
		
		return 3; // Default fallback
	};

	const blanksCount = getBlanksCount();
	const parenthesesBlanks = parseQuestionText(section.question || '');
	const bracketBlanks = parseBlankPlaceholders(section.question || '');
	const hasInlineBlanks = parenthesesBlanks.length > 0 || bracketBlanks.length > 0;

	// Function to render question text with inline input fields
	const renderQuestionWithInputs = () => {
		if (!section.question) return <span>{section.question}</span>;
		
		if (parenthesesBlanks.length === 0 && bracketBlanks.length === 0) {
			return <span>{section.question}</span>;
		}
		
		// Use parentheses blanks if available, otherwise use bracket blanks
		const blanksToUse = parenthesesBlanks.length > 0 ? parenthesesBlanks : bracketBlanks;
		
		return (
			<>
				{blanksToUse.map((blank, index) => (
					<span key={`blank-${section.id}-${index}`}>
						{blank.beforeText}
						<input
							type="text"
							value={getBlankValue(index)}
							onChange={(e) => handleBlankChange(index, e.target.value)}
							placeholder=""
							className="mx-2 px-2 py-1 border-b-2 border-gray-400 focus:outline-none focus:border-gray-600 text-base text-center bg-transparent transition-all duration-200 min-w-[200px]"
							autoComplete="off"
						/>
						{index === blanksToUse.length - 1 && blank.afterText}
					</span>
				))}
			</>
		);
	};

	// If we have inline blanks, render the question with embedded inputs
	if (hasInlineBlanks) {
		return (
			<div className="space-y-4">
				<div className="text-base leading-relaxed text-gray-800">
					{renderQuestionWithInputs()}
				</div>
				
				{/* Progress indicator */}
				<div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-gray-700">
							Filled blanks:
						</span>
						<div className="flex items-center gap-2">
							<span className="text-lg font-bold text-gray-600">
								{Object.keys(localAnswers).filter(key => localAnswers[key]?.trim()).length}
							</span>
							<span className="text-gray-600">/</span>
							<span className="text-base font-semibold text-gray-800">
								{blanksCount}
							</span>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Fallback to traditional separate input fields
	return (
		<div className="space-y-3">
			{Array.from({ length: blanksCount }, (_, index) => (
				<div key={index}>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						Blank {index + 1}:
						{section.correctAnswers?.[index] && (
							<span className="text-xs text-gray-500 ml-2">
								(Expected: {section.correctAnswers[index]})
							</span>
						)}
					</label>
					<input 
						type="text" 
						value={getBlankValue(index)} 
						onChange={(e) => handleBlankChange(index, e.target.value)} 
						placeholder={section.hint || `Enter answer for blank ${index + 1}`} 
						className="w-full px-3 py-2 text-base border-b-2 border-gray-400 focus:outline-none focus:border-gray-600 bg-transparent transition-all duration-200" 
					/>
				</div>
			))}
		</div>
	);
};

const MatchingSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Handle different matching structures
	let left, right;
	
	// Parse left and right columns from multiple data structures
	if (section.answers && Array.isArray(section.answers)) {
		// Use answers array for right column
		left = section.options || section.leftColumn || section.questions || [];
		right = section.answers;
	} else if (section.leftColumn && section.rightColumn) {
		// Use separate left and right column arrays
		left = section.leftColumn;
		right = section.rightColumn;
	} else if (section.questions && section.answers) {
		// Use questions and answers arrays
		left = section.questions;
		right = section.answers;
	} else if (section.pairs && Array.isArray(section.pairs)) {
		// Use pairs array structure
		left = section.pairs.map(pair => pair.left || pair.question || pair.item);
		right = section.pairs.map(pair => pair.right || pair.answer || pair.definition);
	} else {
		// Fallback to pairId/sortOrder logic
		left = (section.options || []).filter(opt => ((opt.pairId ?? opt.sortOrder) % 2 === 0));
		right = (section.options || []).filter(opt => ((opt.pairId ?? opt.sortOrder) % 2 === 1));
	}
	
	const matches = answer || {};
	
	const handleChange = (leftId, rightId) => {
		const next = { ...(matches || {}) };
		next[leftId] = rightId;
		onAnswer(next);
	};
	
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div className="space-y-3">
				{left.map((opt, idx) => (
					<div key={opt.id || idx} className="p-3">
						<div className="mb-2 text-sm text-gray-700">{opt.text || opt}</div>
						<select 
							value={matches?.[opt.id || idx] || ''} 
							onChange={(e) => handleChange(opt.id || idx, e.target.value)} 
							disabled={isAnswered} 
							className="w-full p-2"
						>
							<option value="">Select match...</option>
							{right.map((r, rIdx) => (
								<option key={r.id || rIdx} value={r.id || rIdx}>
									{r.text || r}
								</option>
							))}
						</select>
					</div>
				))}
			</div>
			<div className="space-y-3">
				{right.map((opt, idx) => (
					<div key={opt.id || idx} className="p-3">
						{opt.text || opt}
					</div>
				))}
			</div>
		</div>
	);
};

const OrderingSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Handle items array structure - support both items and options
	const items = section.items || section.options || [];
	const [orderedItems, setOrderedItems] = useState([]);
	const [draggedItem, setDraggedItem] = useState(null);

	// Initialize ordered items
	useEffect(() => {
		if (answer && answer.order) {
			// Use existing answer order
			if (Array.isArray(answer.order)) {
				// Handle array of IDs
				const ordered = answer.order.map(id => {
					// Find item by ID if items have IDs, otherwise use index
					const found = items.find(item => 
						typeof item === 'object' ? item.id === id : item === id
					);
					return found || id;
				}).filter(Boolean);
				setOrderedItems(ordered);
			} else {
				// Handle object with order property
				const ordered = answer.order.map(id => {
					const found = items.find(item => 
						typeof item === 'object' ? item.id === id : item === id
					);
					return found || id;
				}).filter(Boolean);
				setOrderedItems(ordered);
			}
		} else {
			// Initialize with default order
			const initialOrder = [...items];
			// Sort by sortOrder if available, otherwise keep original order
			if (items.length > 0 && typeof items[0] === 'object' && items[0].sortOrder !== undefined) {
				initialOrder.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
			}
			setOrderedItems(initialOrder);
		}
	}, [answer, items]);

	const handleDragStart = (e, item) => {
		// Allow dragging even after answering - users should be able to reorder
		setDraggedItem(item);
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragOver = (e) => {
		// Allow drag over even after answering
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleDrop = (e, targetItem) => {
		if (!draggedItem) return;
		e.preventDefault();

		const draggedIndex = orderedItems.findIndex(item => 
			typeof item === 'object' ? item.id === draggedItem.id : item === draggedItem
		);
		const targetIndex = orderedItems.findIndex(item => 
			typeof item === 'object' ? item.id === targetItem.id : item === targetItem
		);

		if (draggedIndex !== -1 && targetIndex !== -1) {
			const newOrder = [...orderedItems];
			const [removed] = newOrder.splice(draggedIndex, 1);
			newOrder.splice(targetIndex, 0, removed);
			setOrderedItems(newOrder);
			
			// Update answer - handle both object and string items
			const orderIds = newOrder.map(item => 
				typeof item === 'object' ? item.id : item
			);
			onAnswer({ order: orderIds });
		}
		setDraggedItem(null);
	};

	const moveItem = (fromIndex, toIndex) => {
		// Allow moving even after answering
		const newOrder = [...orderedItems];
		const [removed] = newOrder.splice(fromIndex, 1);
		newOrder.splice(toIndex, 0, removed);
		setOrderedItems(newOrder);
		
		// Update answer - handle both object and string items
		const orderIds = newOrder.map(item => 
			typeof item === 'object' ? item.id : item
		);
		onAnswer({ order: orderIds });
	};

	const moveItemUp = (index) => {
		if (index > 0) {
			moveItem(index, index - 1);
		}
	};

	const moveItemDown = (index) => {
		if (index < orderedItems.length - 1) {
			moveItem(index, index + 1);
		}
	};

	const resetOrder = () => {
		// Allow reset even after answering
		const resetOrder = [...items];
		// Sort by sortOrder if available, otherwise keep original order
		if (items.length > 0 && typeof items[0] === 'object' && items[0].sortOrder !== undefined) {
			resetOrder.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		}
		setOrderedItems(resetOrder);
		
		// Update answer - handle both object and string items
		const orderIds = resetOrder.map(item => 
			typeof item === 'object' ? item.id : item
		);
		onAnswer({ order: orderIds });
	};

	// Handle case where there are no options yet
	if (!items || items.length === 0) {
		return (
			<div className="p-4">
				<div className="flex items-center gap-2 text-gray-800">
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span className="font-medium">Question Preview</span>
				</div>
				<p className="text-gray-700 mt-2">
					This is an ordering question. The items to arrange are not yet configured. 
					When fully configured, you'll see a list of items that you can drag and drop to reorder.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Instructions */}
			<div className="p-4">
				<p className="text-sm text-gray-800">
					<strong>Instructions:</strong> Arrange the items below in the correct order. 
					You can drag and drop items to reorder them, or use the up/down arrow buttons.
				</p>
			</div>

			{/* Ordering Interface */}
			<div>
				<h4 className="text-lg font-semibold text-gray-800 mb-4 text-center">
					Arrange Items in Correct Order
				</h4>
				
				<div className="space-y-3">
					{orderedItems.map((item, index) => (
						<div
							key={typeof item === 'object' ? item.id : item}
							draggable={true}
							onDragStart={(e) => handleDragStart(e, item)}
							onDragOver={handleDragOver}
							onDrop={(e) => handleDrop(e, item)}
							className={`p-4 transition-all duration-200 cursor-move hover:bg-gray-50 ${
								draggedItem && (typeof draggedItem === 'object' ? draggedItem.id : draggedItem) === (typeof item === 'object' ? item.id : item) ? 'opacity-50' : ''
							}`}
						>
							<div className="flex items-center justify-between">
								{/* Position and Content */}
								<div className="flex items-center gap-4 flex-1">
									<div className="flex items-center gap-2">
										<span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-bold min-w-[2rem] text-center">
											{index + 1}
										</span>
									</div>
									
									<div className="flex-1">
										<div className="text-gray-800 font-medium">
											{typeof item === 'object' ? (item.text || item.id) : item}
										</div>
									</div>
								</div>

								{/* Move Controls */}
								<div className="flex items-center gap-1 ml-4">
									<button
										onClick={() => moveItemUp(index)}
										disabled={index === 0}
										className={`p-1 rounded ${
											index === 0 
												? 'text-gray-300 cursor-not-allowed' 
												: 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
										}`}
									>
										↑
									</button>
									<button
										onClick={() => moveItemDown(index)}
										disabled={index === orderedItems.length - 1}
										className={`p-1 rounded ${
											index === orderedItems.length - 1 
												? 'text-gray-300 cursor-not-allowed' 
												: 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
										}`}
									>
										↓
									</button>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Reset Button */}
			<div className="flex justify-center">
				<button
					onClick={resetOrder}
					className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-50 transition-colors"
				>
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
					Reset to Original Order
				</button>
			</div>

			{/* Progress Indicator */}
			<div className="text-sm text-gray-600 text-center">
				Items to order: {orderedItems.length}
			</div>
		</div>
	);
};

const AccountingTableSection = ({ section, answer, onAnswer, isAnswered }) => {
	// Ensure all fields are strings before concatenation
	const title = typeof section.title === 'string' ? section.title : '';
	const question = typeof section.question === 'string' ? section.question : '';
	const tableData = typeof section.tableData === 'string' ? section.tableData : '';
	
	const text = `${title} ${question} ${tableData}`;
	const hasCrDr = /(\bDr\b|\bCr\b)/i.test(text);

	// Parse [BLANK] placeholders from section.tableData for input mode
	const parseBlanks = () => {
		if (!section.tableData || typeof section.tableData !== 'string') return [];
		const lines = section.tableData.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
		const blanks = [];
		for (let i = 0; i < lines.length; i++) {
			if (/\[BLANK\]/i.test(lines[i])) {
				// Use previous non-blank line as the label if possible
				let label = `Field ${blanks.length + 1}`;
				for (let j = i - 1; j >= 0; j--) {
					if (!/\[BLANK\]/i.test(lines[j]) && lines[j].length > 0) { label = lines[j]; break; }
				}
				blanks.push({ id: `blank_${blanks.length + 1}`, label });
			}
		}
		return blanks;
	};
	const blanks = parseBlanks();

	const handleBlankChange = (id, value) => {
		const current = answer || {};
		const next = { ...current, [id]: value };
		onAnswer(next);
	};

	if (hasCrDr) {
		return (
			<div className="bg-gray-100 overflow-hidden">
				<table className="w-full border-collapse">
					<thead>
						<tr className="bg-white">
							<th className="px-4 py-3 text-left font-bold text-gray-800 text-base">Description</th>
							<th className="py-3 text-end font-bold text-gray-800 text-base">Dr</th>
							<th className="px-4 py-3 text-left font-bold text-gray-800 text-base">Description</th>
							<th className="py-3 text-end font-bold text-gray-800 text-base">Cr</th>
						</tr>
						<tr className="bg-white">
							<th className="px-4 text-left font-bold text-gray-800 text-base">&nbsp;</th>
							<th className="text-end font-bold text-gray-800 text-base">$</th>
							<th className="px-4 text-left font-bold text-gray-800 text-base">&nbsp;</th>
							<th className="text-end font-bold text-gray-800 text-base">$</th>
						</tr>
					</thead>
					<tbody>
						<tr className="bg-gray-100">
							<td className="px-4 py-3">Opening balance</td>
							<td className="px-4 py-3 text-right">32,400</td>
							<td className="px-4 py-3">Sales returns</td>
							<td className="px-4 py-3 text-right">8,200</td>
						</tr>
						<tr className="bg-gray-100">
							<td className="px-4 py-3">Sales</td>
							<td className="px-4 py-3 text-right">104,500</td>
							<td className="px-4 py-3">Bank</td>
							<td className="px-4 py-3 text-right">118,000</td>
						</tr>
						<tr className="bg-gray-100">
							<td className="px-4 py-3">&nbsp;</td>
							<td className="px-4 py-3 text-right">&nbsp;</td>
							<td className="px-4 py-3">Financial position c/fwd</td>
							<td className="px-4 py-3 text-right">10,700</td>
						</tr>
						<tr className="bg-gray-200">
							<td className="px-4 py-3 font-bold">Total</td>
							<td className="px-4 py-3 text-right font-bold">136,900</td>
							<td className="px-4 py-3 font-bold">Total</td>
							<td className="px-4 py-3 text-right font-bold">136,900</td>
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	// If BLANKs present, render inputs list; else show example financial statement table
	if (blanks.length > 0) {
		return (
			<div className="space-y-3">
				{blanks.map((b, idx) => (
					<div key={b.id} className="flex items-center gap-3">
						<label className="min-w-[220px] text-sm text-gray-700">{b.label}:</label>
						<input
							type="text"
							value={(answer && answer[b.id]) || ''}
							onChange={(e) => handleBlankChange(b.id, e.target.value)}
							disabled={isAnswered}
							className="flex-1 px-3 py-2 rounded"
							placeholder={`Enter value for ${b.label}`}
						/>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="bg-gray-100 overflow-hidden">
			<table className="w-full border-collapse">
				<thead>
					<tr className="bg-white">
						<th className="px-4 py-3 text-left font-bold text-gray-800 text-base">&nbsp;</th>
						<th className="px-4 py-3 text-center font-bold text-gray-800 text-base">Wallace Co</th>
						<th className="px-4 py-3 text-center font-bold text-gray-800 text-base">Bruce Co</th>
					</tr>
					<tr className="bg-white">
						<th className="px-4 py-3 text-left font-bold text-gray-800 text-base">&nbsp;</th>
						<th className="px-4 py-3 text-center font-bold text-gray-800 text-base">$'000</th>
						<th className="px-4 py-3 text-center font-bold text-gray-800 text-base">$'000</th>
					</tr>
				</thead>
				<tbody>
					<tr className="bg-gray-100">
						<td className="px-4 py-3 font-semibold">Non-current assets:</td>
						<td className="px-4 py-3 text-center">&nbsp;</td>
						<td className="px-4 py-3 text-center">&nbsp;</td>
					</tr>
					<tr className="bg-gray-100">
						<td className="px-4 py-3 pl-8">Property, plant & equipment</td>
						<td className="px-4 py-3 text-right">29,860</td>
						<td className="px-4 py-3 text-right">14,895</td>
					</tr>
					<tr className="bg-gray-100">
						<td className="px-4 py-3 pl-8">Investment</td>
						<td className="px-4 py-3 text-right">9,000</td>
						<td className="px-4 py-3 text-right">0</td>
					</tr>
					<tr className="bg-gray-200">
						<td className="px-4 py-3 font-bold">Subtotal for Non-current assets</td>
						<td className="px-4 py-3 text-right font-bold">38,860</td>
						<td className="px-4 py-3 text-right font-bold">14,895</td>
					</tr>
					<tr className="bg-gray-100">
						<td className="px-4 py-3 font-semibold">Current assets</td>
						<td className="px-4 py-3 text-center">&nbsp;</td>
						<td className="px-4 py-3 text-center">&nbsp;</td>
					</tr>
					<tr className="bg-gray-200">
						<td className="px-4 py-3 font-bold">Subtotal for Current assets</td>
						<td className="px-4 py-3 text-right font-bold">12,010</td>
						<td className="px-4 py-3 text-right font-bold">11,697</td>
					</tr>
					<tr className="bg-gray-300">
						<td className="px-4 py-3 font-bold">Grand Total</td>
						<td className="px-4 py-3 text-right font-bold">50,870</td>
						<td className="px-3 text-right font-bold">26,592</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
};

const CompoundChoiceSection = ({ section, answer, onAnswer, isAnswered, mainQuestion }) => {
	const isMultiple = section.allowMultiple !== false;
	const selected = (answer?.selectedOptions) || [];
	
	// For compound choice questions, we need to check if the main question has answerSections
	// If it does, we should render each answerSection as a separate question
	if (mainQuestion && mainQuestion.answerSections && Array.isArray(mainQuestion.answerSections) && mainQuestion.answerSections.length > 0) {
		return (
			<div className="space-y-6">
				{mainQuestion.answerSections.map((answerSection, sectionIndex) => (
					<div key={answerSection.id || sectionIndex} className="p-4">
						{/* Answer Section Title */}
						<h5 className="text-sm font-semibold text-gray-800 mb-3">
							{answerSection.title}
						</h5>
						
						{/* Answer Section Options */}
						<div className="space-y-2">
							{answerSection.options && Array.isArray(answerSection.options) && answerSection.options.map((option, optionIndex) => {
								const optionId = `${answerSection.id || sectionIndex}-${option.id || optionIndex}`;
								const isSelected = selected.includes(optionId);
								
								const handleToggle = () => {
									if (isAnswered) return;
									let next = [];
									if (isMultiple) {
										next = isSelected ? selected.filter(x => x !== optionId) : [...selected, optionId];
									} else {
										// For single choice, only allow one selection per answerSection
										next = [optionId];
									}
									onAnswer({ selectedOptions: next });
								};
								
								return (
									<label key={optionId} className="flex items-start gap-3 p-2 cursor-pointer hover:bg-gray-50">
										<input 
											type={isMultiple ? 'checkbox' : 'radio'} 
											name={`cc-${section.id}-${answerSection.id || sectionIndex}`} 
											checked={isSelected} 
											onChange={handleToggle} 
											disabled={isAnswered} 
											className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500" 
										/>
										<span className="text-sm text-gray-700">
											{String.fromCharCode(65 + optionIndex)}. {option.text || option}
										</span>
									</label>
								);
							})}
						</div>
					</div>
				))}
			</div>
		);
	}
	
	// Fallback to original logic for backward compatibility
	const getOptions = () => {
		if (section.options && Array.isArray(section.options)) {
			return section.options;
		}
		if (section.choices && Array.isArray(section.choices)) {
			return section.choices;
		}
		if (section.items && Array.isArray(section.items)) {
			return section.items;
		}
		if (section.alternatives && Array.isArray(section.alternatives)) {
			return section.alternatives;
		}
		if (section.possibilities && Array.isArray(section.possibilities)) {
			return section.possibilities;
		}
		return [];
	};

	const options = getOptions();
	
	const toggle = (id) => {
		if (isAnswered) return;
		let next = [];
		if (isMultiple) {
			next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
		} else {
			next = [id];
		}
		onAnswer({ selectedOptions: next });
	};
	
	return (
		<div className="space-y-2">
			{options.map((opt, idx) => (
				<label key={opt.id || idx} className="flex items-start gap-3 p-2 cursor-pointer hover:bg-gray-50">
					<input 
						type={isMultiple ? 'checkbox' : 'radio'} 
						name={`cc-${section.id}`} 
						checked={selected.includes(opt.id || opt)} 
						onChange={() => toggle(opt.id || opt)} 
						disabled={isAnswered} 
						className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-blue-500" 
					/>
					<span className="text-sm text-gray-700">
						{String.fromCharCode(65 + idx)}. {opt.text || opt}
					</span>
				</label>
			))}
		</div>
	);
};

const EssaySection = ({ section, answer, onAnswer, isAnswered }) => {
	const [wordCount, setWordCount] = useState(0);
	
	const handleTextChange = (e) => {
		const text = e.target.value;
		const words = text.trim().split(/\s+/).filter(word => word.length > 0);
		setWordCount(words.length);
		onAnswer({ text, wordCount: words.length });
	};

	return (
		<div className="space-y-4">
			<div className="p-4">
				<p className="text-sm text-gray-800">
					<strong>Instructions:</strong> Provide a comprehensive written response to the question above.
				</p>
			</div>
			
			<textarea
				value={answer?.text || ''}
				onChange={handleTextChange}
				disabled={isAnswered}
				placeholder="Type your essay answer here..."
				className="w-full h-48 p-4 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
			/>
			
			<div className="flex justify-between items-center text-sm text-gray-600">
				<span>Word count: {wordCount}</span>
				<span className="text-blue-600">Essay Question</span>
			</div>
		</div>
	);
};

const ShortAnswerSection = ({ section, answer, onAnswer, isAnswered }) => {
	const [charCount, setCharCount] = useState(0);
	
	const handleTextChange = (e) => {
		const text = e.target.value;
		setCharCount(text.length);
		onAnswer({ text, charCount: text.length });
	};

	return (
		<div className="space-y-4">
			<div className="p-4">
				<p className="text-sm text-gray-800">
					<strong>Instructions:</strong> Provide a concise answer to the question above.
				</p>
			</div>
			
			<input
				type="text"
				value={answer?.text || ''}
				onChange={handleTextChange}
				placeholder="Enter your short answer..."
				className="w-full p-3 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
			/>
			
			<div className="flex justify-between items-center text-sm text-gray-600">
				<span>Character count: {charCount}</span>
				<span className="text-green-600">Short Answer Question</span>
			</div>
		</div>
	);
};

const NumericalSection = ({ section, answer, onAnswer, isAnswered }) => {
	const handleNumberChange = (e) => {
		const value = e.target.value;
		onAnswer({ value: parseFloat(value) || 0 });
	};

	return (
		<div className="space-y-4">
			<div className="p-4">
				<p className="text-sm text-gray-800">
					<strong>Instructions:</strong> Enter the numerical answer to the question above.
				</p>
			</div>
			
			<input
				type="number"
				step="any"
				value={answer?.value || ''}
				onChange={handleNumberChange}
				disabled={isAnswered}
				placeholder="Enter numerical value..."
				className="w-full p-3 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
			/>
			
			<div className="text-sm text-purple-600 text-center">
				Numerical Question
			</div>
		</div>
	);
};

const CalculationSection = ({ section, answer, onAnswer, isAnswered }) => {
	const [showWork, setShowWork] = useState(false);
	
	const handleAnswerChange = (e) => {
		onAnswer({ finalAnswer: e.target.value });
	};
	
	const handleWorkChange = (e) => {
		onAnswer({ work: e.target.value, finalAnswer: answer?.finalAnswer || '' });
	};

	return (
		<div className="space-y-4">
			<div className="p-4">
				<p className="text-sm text-gray-800">
					<strong>Instructions:</strong> Show your calculations and provide the final answer.
				</p>
			</div>
			
			<div className="space-y-3">
				<label className="flex items-center gap-2">
					<input
						type="checkbox"
						checked={showWork}
						onChange={(e) => setShowWork(e.target.checked)}
						className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
					/>
					<span className="text-sm text-gray-700">Show calculation work</span>
				</label>
				
				{showWork && (
					<textarea
						value={answer?.work || ''}
						onChange={handleWorkChange}
						disabled={isAnswered}
						placeholder="Show your calculation steps here..."
						className="w-full h-32 p-3 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
					/>
				)}
				
				<input
					type="text"
					value={answer?.finalAnswer || ''}
					onChange={handleAnswerChange}
					disabled={isAnswered}
					placeholder="Enter final answer..."
					className="w-full p-3 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
				/>
			</div>
			
			<div className="text-sm text-orange-600 text-center">
				Calculation Question
			</div>
		</div>
	);
};

const UnknownSectionType = ({ section }) => {
	return (
		<div className="p-3 text-sm text-red-800">
			Unknown section type: {section.type}. Please contact support.
		</div>
	);
};

export default EnhancedCompoundQuestion;
